// Is this JPEG safe to print? (2026-10-07)
//
// ─── WHY ────────────────────────────────────────────────────────────────────
//
// A3 duplex sheets came out with « [PDF to PS Conversion Error:] "A drawing
// error occurred." » on one side, the other side fine. The failing pages
// carried PROGRESSIVE JPEGs — WhatsApp re-encodes photos that way (900×1600,
// 1280×576…). They were embedded byte-for-byte (the verbatim pass-through in
// pdfGenerator's normalisePhotoForPdf): PDF 1.3+ and every screen viewer
// decode them, but PostScript's DCTDecode does not, so the PDF→PostScript step
// on the way to the printer (Acrobat's print path, the printer's RIP) dies on
// that page. #WA-168784: 60 of its 75 photos progressive; #WA-402224: one.
//
// What PostScript's DCT filter takes (Adobe Tech Note 5116): sequential
// Huffman JPEG — baseline (SOF0) or extended sequential (SOF1) — 8-bit,
// 1 (gray) or 3 (YCbCr) components. Anything else is re-encoded to baseline
// before it goes into a print PDF.
//
// Pure. Guard: printSafeJpeg.test.ts

/** Why a JPEG cannot go into a print PDF as-is, or null when it can. */
export function jpegPrintIssue(bytes: Uint8Array): string | null {
  if (bytes.length < 4 || bytes[0] !== 0xff || bytes[1] !== 0xd8) return 'not a JPEG (no SOI marker)';
  let i = 2;
  let frame: { marker: number; precision: number; components: number } | null = null;
  while (i + 3 < bytes.length) {
    if (bytes[i] !== 0xff) return 'corrupt JPEG (marker expected)';
    const m = bytes[i + 1];
    if (m === 0xff) { i += 1; continue; }                       // fill byte
    if (m === 0xd8 || m === 0x01 || (m >= 0xd0 && m <= 0xd7)) { i += 2; continue; }
    if (m === 0xd9) break;                                     // EOI before any scan
    const len = (bytes[i + 2] << 8) | bytes[i + 3];
    if (len < 2 || i + 2 + len > bytes.length) return 'corrupt JPEG (segment runs past the end)';
    // SOFn: C0–CF except C4 (DHT), C8 (JPG), CC (DAC)
    if (m >= 0xc0 && m <= 0xcf && m !== 0xc4 && m !== 0xc8 && m !== 0xcc) {
      frame = { marker: m, precision: bytes[i + 4], components: bytes[i + 9] };
    }
    if (m === 0xda) break;                                     // start of scan: header read
    i += 2 + len;
  }
  if (!frame) return 'corrupt JPEG (no frame header)';
  if (frame.marker === 0xc2 || frame.marker === 0xc6 || frame.marker === 0xca || frame.marker === 0xce) {
    return 'progressive JPEG (PostScript cannot decode it)';
  }
  if (frame.marker !== 0xc0 && frame.marker !== 0xc1) {
    return `unsupported JPEG type (SOF 0x${frame.marker.toString(16)})`;
  }
  if (frame.precision !== 8) return `${frame.precision}-bit JPEG (PostScript takes 8-bit)`;
  if (frame.components !== 1 && frame.components !== 3) {
    return `${frame.components}-component JPEG (CMYK or unusual colour — re-encoded to RGB)`;
  }
  // A cut-off file decodes on screen (grey bottom) and fails in a RIP.
  let end = bytes.length;
  while (end > 2 && bytes[end - 1] === 0x00) end -= 1;
  if (!(bytes[end - 2] === 0xff && bytes[end - 1] === 0xd9)) return 'truncated JPEG (no end marker)';
  return null;
}
